import { useState, useRef } from 'react';
import { Container, Box } from 'theme-ui';

import { withInView } from '@utils';
import { Section } from '@snippets';
import { NavigationButton } from './ProductCarousel/Navigation/NavigationButton';

import { Schema } from './ProductCarouselToggle.schema';
import { themed } from './ProductCarouselToggle.theme';
import { ProductCarousel } from './ProductCarousel';

// Minimal test component
export const ProductCarouselToggle = withInView(
  themed(({ theme, cms }) => {
    const { content, toggles, section } = cms || {};
    const [activeToggle, setActiveToggle] = useState(0);
    const [swiper, setSwiper] = useState(null);
    const [isStart, setIsStart] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    // Extract alignment settings
    const {
      headingAlign = 'left',
      headingAlignMobile = 'center',
      togglesAlign = 'center',
      togglesAlignMobile = 'center',
    } = content || {};

    // Helper function to convert text alignment to flex justification
    const getFlexAlignment = (align) => {
      switch (align) {
        case 'left':
          return 'flex-start';
        case 'right':
          return 'flex-end';
        case 'center':
        default:
          return 'center';
      }
    };

    // Check if we have enough products to show navigation
    const currentProducts = toggles[activeToggle]?.products || [];
    const showNavigation = currentProducts.length >= 4;

    // Basic error handling
    if (!cms) {
      return (
        <Section section={{ bgColor: '#ffffff' }}>
          <Container>
            <Box sx={{ textAlign: 'center', py: 4 }}>Loading...</Box>
          </Container>
        </Section>
      );
    }

    if (!toggles?.length) {
      return (
        <Section section={section || { bgColor: '#ffffff' }}>
          <Container>
            <Box sx={{ textAlign: 'center', py: 4 }}>No toggles configured</Box>
          </Container>
        </Section>
      );
    }

    return (
      <Section section={section || { bgColor: '#ffffff' }}>
        <Container
          data-comp={ProductCarouselToggle.displayName}
          sx={theme?.wrapper || {}}
        >
          {/* Header with Navigation */}
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 3,
              position: 'relative',
              zIndex: 0,
            }}
          >
            <Box
              sx={{
                textAlign: [headingAlignMobile, headingAlign],
                justifyContent: [
                  getFlexAlignment(headingAlignMobile),
                  getFlexAlignment(headingAlign),
                ],
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: [11, 15],
                flexDirection: ['column', 'row'],
              }}
            >
              <Box
                as="h2"
                sx={{
                  width: ['100%', 'auto'],
                  margin: 0,
                  fontSize: ['36px', '56px'],
                  fontWeight: 400,
                  textAlign: [headingAlignMobile, headingAlign],
                  fontFamily: 'Reckless Neue',
                }}
              >
                {content?.heading || 'Shop By Color'}
              </Box>

              {/* Toggles */}
              <Box
                sx={{
                  width: ['100%', 'auto'],
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: [
                    getFlexAlignment(togglesAlignMobile),
                    getFlexAlignment(togglesAlign),
                  ],
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    mb: 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    backgroundColor: (t) => t.colors.gray,
                    borderRadius: '100px',
                    p: '8px',
                  }}
                >
                  {toggles.map((toggle, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveToggle(index)}
                      style={{
                        backgroundColor:
                          activeToggle === index ? '#fff' : 'transparent',
                        color: '#000',
                        border: 'none',
                        padding: '16px',
                        borderRadius: '100px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        lineHeight: '1',
                      }}
                    >
                      {toggle?.label || `Toggle ${index + 1}`}
                    </button>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Navigation Buttons - Desktop Only */}
            {showNavigation && (
              <Box
                sx={{
                  display: ['none', 'flex'],
                  gap: 2,
                  alignItems: 'center',
                  position: 'absolute',
                  right: 0,
                }}
              >
                <NavigationButton
                  onClick={() => swiper?.slidePrev()}
                  inactive={isStart}
                  count={showNavigation}
                  alt="Previous products"
                  direction="prev"
                  sx={{
                    position: 'static',
                    transform: 'none',
                  }}
                />

                <NavigationButton
                  onClick={() => swiper?.slideNext()}
                  src="/svgs/arrow-right.svg#arrow-right"
                  alt="Next products"
                  inactive={isEnd}
                  isNext
                  direction="next"
                  count={showNavigation}
                  sx={{
                    position: 'static',
                    transform: 'none',
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Products */}
          <Box>
            <ProductCarousel
              products={toggles[activeToggle]?.products || []}
              swiper={swiper}
              setSwiper={setSwiper}
              setIsStart={setIsStart}
              setIsEnd={setIsEnd}
            />
          </Box>
        </Container>
      </Section>
    );
  }),
  { triggerOnce: true }
);

ProductCarouselToggle.displayName = 'ProductCarouselToggle';
ProductCarouselToggle.Schema = Schema;
